import {Tag} from 'antd';
import React from 'react';
import {
  AGENT_ICONS,
  AGENT_PREFIX,
  expensePolicyStatusConstants,
  expenseReviewStatusConstants,
  reviewApproverTypeConstants,
  userRolesConstants,
} from '../../constants';

const {ANY_ADMIN, AUTO_APPROVAL} = reviewApproverTypeConstants;

/**
 * Collection of helper functions for working with review and approval workflows.
 */
export const reviewHelpers = {
  /**
   * Tells whether an email is an agent's key (`agent_expenses`) and not a person's address.
   * @param {string} [email] - Author or approver email.
   * @returns {boolean} True when it starts with `agent_` and has no `@`.
   * @example
   * reviewHelpers.isAgentEmail('agent_expenses'); // true
   */
  isAgentEmail: (email) => typeof email === 'string' && email.startsWith(AGENT_PREFIX) && !email.includes('@'),
  /**
   * Gives the icon component of an agent, or null for anyone else.
   * @param {string} [email] - Author or approver email.
   * @returns {Function|null} The icon component, e.g. `CuboIcon` for `agent_expenses`.
   * @example
   * reviewHelpers.getAgentIcon('agent_expenses'); // CuboIcon
   */
  getAgentIcon: (email) => (reviewHelpers.isAgentEmail(email) && AGENT_ICONS[email]) || null,
  /**
   * Returns the approval row an agent approved, or null.
   * @param {Object|null} data - Expense with an approval array.
   * @returns {Object|null} The first row whose approver is an agent and whose `is_approved` is true.
   * @example
   * reviewHelpers.getAgentApproval(expense); // {approver: {email: 'agent_expenses'}, is_approved: true}
   */
  getAgentApproval: (data) => (data?.approval || []).find(
    review => reviewHelpers.isAgentEmail(review?.approver?.email) && review?.is_approved === true
  ) || null,
  /**
   * Tells whether a role may undo an agent's approval. The backend allows the Admin role only,
   * so Bookkeeper and Support (which `teamsHelpers.isAdmin` also accepts) are refused.
   * @param {string} [role] - A userRolesConstants value.
   * @returns {boolean} True only for ADMIN.
   * @example
   * reviewHelpers.canRoleUndoAgentApproval(userRolesConstants.ADMIN); // true
   */
  canRoleUndoAgentApproval: (role) => role === userRolesConstants.ADMIN,
  /**
   * Tells whether an Admin may still undo the agent's approval. This is the backend's own test:
   * the expense is approved, the agent's row is approved and no other row has voted.
   * @param {Object|null} expense - Expense with `review_status` and an approval array.
   * @returns {boolean} True when the backend would accept the undo.
   * @example
   * reviewHelpers.canUndoAgentApproval(expense); // true right after Cubo approved
   */
  canUndoAgentApproval: (expense) => {
    const agentRow = reviewHelpers.getAgentApproval(expense);
    return expense?.review_status === expenseReviewStatusConstants.APPROVED
      && Boolean(agentRow)
      && expense.approval.every(review => review === agentRow || review?.is_approved == null);
  },
  /**
   * Checks whether a given employee is listed as an approver in the approval array.
   * @param {Object|null} data - Entity object containing an approval array.
   * @param {string} employeeEmail - Email address of the employee to look for.
   * @returns {boolean} True if the employee appears as an approver, false otherwise.
   * @example
   * reviewHelpers.isEmployeeInApprovalList(expense, 'alice@example.com'); // true
   */
  isEmployeeInApprovalList: (data, employeeEmail) => {
    if (!data || !employeeEmail) return false;
    return (data?.approval || []).some(review => review.approver?.email === employeeEmail);
  },
  /**
   * Checks whether a given employee has already approved by verifying the presence of an approval_date.
   * @param {Object|null} data - Entity object containing an approval array.
   * @param {string} employeeEmail - Email address of the employee to check.
   * @returns {boolean} True if the employee has an approval entry with an approval_date.
   * @example
   * reviewHelpers.isAlreadyApprovedByEmployee(expense, 'alice@example.com'); // false
   */
  isAlreadyApprovedByEmployee: (data, employeeEmail) => {
    if (!data || !employeeEmail) return false;
    return (data?.approval || []).some(review => review.approver?.email === employeeEmail && Boolean(review?.approval_date));
  },
  /**
   * Checks whether an admin override approval with an approval_date exists in the approval array.
   * @param {Object|null} data - Entity object containing an approval array.
   * @returns {boolean} True if an admin_override entry has an approval_date set.
   * @example
   * reviewHelpers.isAlreadyApprovedByAdmin(expense); // true
   */
  isAlreadyApprovedByAdmin: (data) => {
    if (!data) return false;
    return (data?.approval || []).some(review => review?.admin_override && Boolean(review?.approval_date));
  },
  /**
   * Determines whether the approval list contains an ANY_ADMIN sentinel approver entry.
   * @param {Object|null} expense - Expense object with an approval array.
   * @returns {boolean} True if any approval entry uses the ANY_ADMIN email sentinel.
   * @example
   * reviewHelpers.isAnyAdminApproval(expense); // false
   */
  isAnyAdminApproval: (expense) => {
    if (!expense || !Array.isArray(expense.approval)) return false;
    return expense.approval.some(review => review.approver?.email === ANY_ADMIN);
  },
  /**
   * Determines whether the approval list contains an AUTO_APPROVAL sentinel approver entry.
   * @param {Object|null} expense - Expense object with an approval array.
   * @returns {boolean} True if any approval entry uses the AUTO_APPROVAL email sentinel.
   * @example
   * reviewHelpers.isAutoApproval(expense); // true
   */
  isAutoApproval: (expense) => {
    if (!expense || !Array.isArray(expense.approval)) return false;
    return expense.approval.some(review => review.approver?.email === AUTO_APPROVAL);
  },
  /**
   * Returns all approval entries that are not AUTO_APPROVAL or ANY_ADMIN sentinels.
   * @param {Object|null} data - Entity object containing an approval array.
   * @returns {Array<Object>} Filtered array of real approver entries.
   * @example
   * reviewHelpers.getApprovals(expense); // [{ approver: { email: 'bob@example.com' }, ... }]
   */
  getApprovals: (data) => (data?.approval || []).filter(review => ![AUTO_APPROVAL, ANY_ADMIN].includes(review.approver?.email)),
  /**
   * Returns all approval entries whose approver email matches the ANY_ADMIN sentinel.
   * @param {Object|null} data - Entity object containing an approval array.
   * @returns {Array<Object>} Filtered array of ANY_ADMIN approval entries.
   * @example
   * reviewHelpers.getAnyAdminApprovals(expense); // [{ approver: { email: '__any_admin__' }, ... }]
   */
  getAnyAdminApprovals: (data) => (data?.approval || []).filter(review => review.approver?.email === ANY_ADMIN),
  /**
   * Returns all approval entries whose approver email matches the AUTO_APPROVAL sentinel.
   * @param {Object|null} data - Entity object containing an approval array.
   * @returns {Array<Object>} Filtered array of AUTO_APPROVAL approval entries.
   * @example
   * reviewHelpers.getAutoApprovals(expense); // [{ approver: { email: '__auto_approval__' }, ... }]
   */
  getAutoApprovals: (data) => (data?.approval || []).filter(review => review.approver?.email === AUTO_APPROVAL),
  /**
   * Returns the first approval entry that has admin_override set and an approval_date, or null.
   * @param {Object|null} data - Entity object containing an approval array.
   * @returns {Object|null} The matching admin override approval entry, or null if not found.
   * @example
   * reviewHelpers.getAdminOverrideApproval(expense); // { admin_override: true, approval_date: '2024-01-15', ... }
   */
  getAdminOverrideApproval: (data) => {
    if (!data || !Array.isArray(data.approval)) return null;
    return (data?.approval || []).find(review => review?.admin_override && Boolean(review?.approval_date)) || null;
  },
  /**
   * Determines whether quick approval is available for the current user on the given entity.
   * @param {Object} params - Parameters object.
   * @param {Object|null} params.data - Entity object containing an approval array.
   * @param {string} params.employeeEmail - Email address of the current user.
   * @param {boolean} [params.isAdmin=true] - Whether the current user has admin privileges.
   * @returns {boolean} True if quick approval is possible.
   * @example
   * reviewHelpers.canQuickApprove({ data: expense, employeeEmail: 'alice@example.com', isAdmin: false }); // true
   */
  canQuickApprove: ({ data, employeeEmail, isAdmin = true }) => {
    if (!data) return false;
    if (isAdmin && reviewHelpers.isAnyAdminApproval(data)) return true;
    return data?.approval?.length === 1 && data.approval[0]?.approver?.email === employeeEmail;
  },
  /**
   * Decides how an approve click proceeds: one click, the override pop-up, or the amount pop-up.
   * @param {Object} params - Parameters object.
   * @param {Object|null} params.data - Expense (or entity) being approved.
   * @param {string} params.employeeEmail - Email address of the current user.
   * @param {boolean} [params.isAdmin=true] - Whether the current user has admin privileges.
   * @param {boolean} [params.adminOverride] - False when the caller says this is not an override.
   * @param {boolean} [params.forcePopup=false] - True when the user chose the amount pop-up (the arrow beside Approve).
   * @returns {'override'|'popup'|'direct'} 'override' replaces the assigned approvers, 'popup' asks for an amount, 'direct' approves in full at once.
   * @example
   * reviewHelpers.getApproveMode({ data: expense, employeeEmail, isAdmin: false }); // 'popup' for an out-of-policy expense
   */
  getApproveMode: ({ data, employeeEmail, isAdmin = true, adminOverride, forcePopup = false }) => {
    if (adminOverride !== false && !reviewHelpers.canQuickApprove({ data, employeeEmail, isAdmin })) return 'override';
    if (forcePopup || data?.policy_status === expensePolicyStatusConstants.OUT_OF_POLICY) return 'popup';
    return 'direct';
  },
  /**
   * Returns a styled Ant Design Tag element displaying the translated label for a given status value.
   * @param {Object} [params={}] - Parameters object.
   * @param {string} params.value - Status key used to look up the status definition.
   * @param {Function} params.t - i18n translation function.
   * @param {Object} params.statuses - Map of status keys to style and label configuration objects.
   * @returns {React.ReactElement|null} A styled Tag element, or null when value is falsy.
   * @example
   * reviewHelpers.getStatusLabel({ value: 'pending', t, statuses: REVIEW_STATUSES });
   */
  getStatusLabel: ({value, t, statuses, prefix, suffix} = {}) => {
    if (!value) return null;
    const statusProps = statuses[value] || {};
    return (
      <Tag style={{
        backgroundColor: statusProps.backgroundColor || '#F3F3F4',
        borderColor: statusProps.backgroundColor ? 'transparent' : '#CDD0D5',
        borderRadius: 16,
        color: statusProps.color || '#9CA1AA',
        height: 24,
        marginRight: 0,
        fontWeight: 400,
        ...((prefix || suffix) && {alignItems: 'center', display: 'inline-flex', gap: prefix ? 8 : 4})
      }}>{prefix}{statusProps.key ? t(statusProps.key) : 'Undefined'}{suffix || (prefix ? '' : ' ')}</Tag>
    );
  },
}
